import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { getSupabaseClient } from "./supabase";

// Auth state ----------------------------------------------------------------

export type SessionStatus = "loading" | "signed_in" | "signed_out";

export interface SessionState {
  status: SessionStatus;
  /** Supabase user UUID — present only when signed in. */
  userId?: string;
  /** Email address used for magic-link — present only when signed in. */
  email?: string;
}

/**
 * Maps a raw Supabase Session (or null) to the portal's SessionState.
 * Pure function — no side-effects, safe to test directly.
 */
export function mapAuthSession(session: Session | null): SessionState {
  if (session === null) {
    return { status: "signed_out" };
  }
  return {
    status: "signed_in",
    userId: session.user.id,
    email: session.user.email,
  };
}

// Magic-link helper ---------------------------------------------------------

/**
 * Sends a magic-link email to the given address.
 * Magic link is the only auth method — no passwords (§2, 05_PORTAL_DESIGN.md).
 */
export async function signInWithOtp(
  email: string,
  options: { redirectTo?: string } = {},
): Promise<{ error: Error | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: options.redirectTo },
  });
  return { error: error as Error | null };
}

/**
 * Signs in with the one-time code from the same sign-in email — the fallback
 * when the magic link cannot be opened (another device, a mail app that breaks
 * links). The email must carry {{ .Token }} (Supabase → Authentication → Email
 * Templates). On success onAuthStateChange delivers the session.
 */
export async function verifyEmailCode(email: string, code: string): Promise<{ error: Error | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.auth.verifyOtp({ email, token: code, type: "email" });
  return { error: error as Error | null };
}

// Session hook --------------------------------------------------------------

/**
 * React hook that subscribes to Supabase auth-state changes and returns the
 * current SessionState.  Starts as `loading` until the initial session resolves.
 */
export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>({ status: "loading" });

  useEffect(() => {
    const supabase = getSupabaseClient();

    // Resolve initial session immediately without waiting for an event.
    supabase.auth.getSession().then(({ data }) => {
      setState(mapAuthSession(data.session));
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setState(mapAuthSession(session));
      },
    );

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  return state;
}
